import { CommentItem, Vendor, VendorRule } from "@finway-group/shared/lib/models"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { VendorService } from "~Shared/services"
import { CacheEnum } from "../consts"
import { invalidateVendorLists } from "./vendor.queries"

type UpdateVendorType = {
    vendorId: string
    updateVendorData: Vendor
    shouldSynchronizeExpenses?: boolean
}

type UpdateVendorRuleType = {
    vendorId: string
    vendorRule: VendorRule
}

type UpdateVendorContractsType = {
    vendorId: string
    vendorContracts: Array<string>
}

type UpdateVendorCommentsType = {
    vendorId: string
    vendorComments: Array<CommentItem>
}

export const useCreateVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (newVendor: Vendor) => VendorService.createOne(newVendor),
        onSuccess: async (createdVendor: Vendor) => {
            await invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, createdVendor.id], createdVendor)
        },
    })
}

export const useUpdateVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, updateVendorData, shouldSynchronizeExpenses }: UpdateVendorType) =>
            VendorService.updateOne({ id: vendorId, vendor: updateVendorData, queryParams: { shouldSynchronizeExpenses } }),
        onSuccess: async (updatedVendor: Vendor) => {
            await invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useSoftDeleteVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ vendorId, updateVendorData }: UpdateVendorType) => {
            const vendorData = new Vendor({ ...updateVendorData, deleted: true, dateDeleted: Date.now() })
            const updatedVendor = await VendorService.updateOne({ id: vendorId, vendor: vendorData })

            return updatedVendor
        },
        onSuccess: async (updatedVendor: Vendor) => {
            await invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useHardDeleteVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (vendorId: string) => VendorService.deleteOne(vendorId),
        onSuccess: async (_, id) => {
            await invalidateVendorLists()
            queryClient.removeQueries([CacheEnum.VENDORS, id])
        },
    })
}

export const useApproveVendorStatus = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (vendorId: string) => VendorService.approveVendorStatus(vendorId),
        onSuccess: async (updatedVendor: Vendor) => {
            await invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useRestoreVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ vendorId, updateVendorData }: UpdateVendorType) => {
            const vendorData = new Vendor({ ...updateVendorData, deleted: false, dateDeleted: undefined })
            const restoredVendor = await VendorService.updateOne({ id: vendorId, vendor: vendorData })

            return restoredVendor
        },
        onSuccess: async (restoredVendor: Vendor) => {
            await invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, restoredVendor.id], restoredVendor)
        },
    })
}

export const useUpdateVendorRule = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, vendorRule }: UpdateVendorRuleType) => VendorService.updateVendorRule(vendorId, vendorRule),
        onSuccess: async (updatedVendor: Vendor) => {
            await queryClient.invalidateQueries({ queryKey: [CacheEnum.VENDORS_LIST] })
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useUpdateVendorContracts = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, vendorContracts }: UpdateVendorContractsType) => VendorService.updateVendorContracts(vendorId, vendorContracts),
        onSuccess: (updatedVendor: Vendor) => {
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useUpdateVendorComments = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, vendorComments }: UpdateVendorCommentsType) => VendorService.updateComment(vendorId, vendorComments),
        onSuccess: (updatedVendor: Vendor) => {
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}
